import {Validators}                                     from '@angular/forms';
import {Injectable}                                     from '@angular/core';
import {Observable}                                     from 'rxjs';
import {map}                                            from 'rxjs/operators';
import {ApTimeSpanUtil}                                 from '../../../ap-utils';
import {nameOf}                                         from '../../../ap-core/utils/ap-name-of';
import {ApDynformsConfigComboBox}                       from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynformsConfigFieldset}                       from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsConfigTextarea}                       from '../../../ap-dynforms/config/ap-dynforms-config-textarea';
import {
  ApDynformsConfigNumerictextbox
}                                                       from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {
  ApDynformsConfigTimepicker
}                                                       from '../../../ap-dynforms/config/ap-dynforms-config-timepicker';
import {ApDynformsValidator}                            from '../../../ap-dynforms/ap-dynforms-validator';
import {
  ApDynformsConfigPlaceholder
}                                                       from '../../../ap-dynforms/config/ap-dynforms-config-placeholder';
import {
  ApDynformsConfigDatepicker
}                                                       from '../../../ap-dynforms/config/ap-dynforms-config-datepicker';
import {DriverStore}                                    from '../../../stores/docu/driver.store';
import {MachineStore}                                   from '../../../stores/docu/machine.store';
import {InstrumentStore}                                from '../../../stores/docu/instrument.store';
import {CampaignYearStore}                              from '../../../stores/login/campaignyear.store';
import {WorktypeUnitStore}                              from '../../../stores/base-data/worktype-unit.store';
import {ApTranslationService}                           from '../../../ap-utils/service/ap-translation.service';
import {FwEntryComponentsService}                       from '../services/fw-entry-components.service';
import {IFwPlanBookingEditData, IFwPlanBookingGridItem} from './fw-plan-booking.types';
import IUnit = Data.Common.IUnit;
import IDriver = Data.DocuContext.Driver.IDriver;
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;

@Injectable()
export class FwPlanBookingFieldsConfig {
  constructor(private driverStore: DriverStore,
              private machineStore: MachineStore,
              private instrumentStore: InstrumentStore,
              private workTypeUnitStore: WorktypeUnitStore,
              private campaignYearStore: CampaignYearStore,
              private translationService: ApTranslationService,
              private fwEntryComponentService: FwEntryComponentsService) {
  }

  /**
   * Creates an edit object from a grid item
   * @param gridItem - The grid item to create an edit object from
   * @returns IFwPlanBookingEditData containing data from grid item
   * @public
   */
  public getEditObject(gridItem: IFwPlanBookingGridItem): IFwPlanBookingEditData {
    const durationInTimeSpan = ApTimeSpanUtil.getTimeSpanFromMilliseconds(gridItem.DurationInMilliseconds, true);
    return {
      ApplicationDate: gridItem.ApplicationDate,
      Start: gridItem.Start,
      Value: gridItem.Value,
      Notes: gridItem.PlanBookCopy.Note,
      Unit: gridItem.PlanBookCopy.Unit?.Id,
      DriverId: gridItem.DriverId,
      MachineId: gridItem.MachineId,
      InstrumentId: gridItem.InstrumentId,
      DurationInHours: durationInTimeSpan.TotalHours,
      HaPerHour: gridItem.HaPerHour
    };
  }

  /**
   * Generates the configuration fields for a modal dialog within the application.
   * @param overviewItem The overview item representing the selected booking grid information.
   * @param editData The editing data that contains the current state of the fields.
   * @param modalDialogChanges$ Observable stream for tracking changes made in the modal dialog.
   * @return An array of fieldsets containing configurations for the modal dialog fields.
   * @public
   */
  public getModalDialogFields(overviewItem: IFwPlanBookingGridItem, editData: IFwPlanBookingEditData,
                              modalDialogChanges$: Observable<IFwPlanBookingEditData>): ApDynformsConfigFieldset[] {
    const campaignYear = this.campaignYearStore.SelectedCampaignYear.Year;
    const minAndMaxRange = this.fwEntryComponentService.getMinAndMaxDateRangesForPlanBook(campaignYear);
    const workTypeUnits = this.workTypeUnitStore.getUnitsByWorktype(overviewItem.PlanBookCopy.WorkType.Id);
    return [
      new ApDynformsConfigFieldset({
        key: 'General',
        legend: 'Global__General_Information',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigDatepicker({
            key: nameOf<IFwPlanBookingEditData>('ApplicationDate'),
            label: 'Nutrients__FertilizationDate',
            minDate: minAndMaxRange.Min,
            maxDate: minAndMaxRange.Max,
            value: editData.ApplicationDate,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'})
            ]
          }),
          new ApDynformsConfigTimepicker({
            key: nameOf<IFwPlanBookingEditData>('Start'),
            label: 'Docu_Ini__StartTime',
            format: 'HH:mm',
            value: editData.Start
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'WorkInformation',
        legend: 'Work_Information',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IFwPlanBookingEditData>('Value'),
            label: 'Worktype_Value',
            value: editData.Value,
            disabled: !Number.isFinite(editData.Value)
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanBookingEditData>('Unit'),
            label: 'Worktype_Unit',
            valueField: nameOf<IUnit>('Id'),
            textField: nameOf<IUnit>('Key'),
            value: editData.Unit,
            options: workTypeUnits,
            disabled: workTypeUnits.length < 2,
            translate: true,
            valuePrimitive: true
          }),
          new ApDynformsConfigTextarea({
            key: nameOf<IFwPlanBookingEditData>('Notes'),
            label: 'Global__Note',
            rows: 2
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'Docu',
        legend: 'Settings__Lbl_Menu_Resources',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanBookingEditData>('MachineId'),
            label: 'Settings__Lbl_Menu_Devices',
            textField: nameOf<IMachine>('NameWithSign'),
            valueField: nameOf<IMachine>('Id'),
            options: this.machineStore.Machines$,
            value: editData.MachineId,
            sort: 1,
            valuePrimitive: true
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanBookingEditData>('InstrumentId'),
            label: 'Docu_Ini__Tool',
            textField: nameOf<IInstrument>('Name'),
            valueField: nameOf<IInstrument>('Id'),
            options: this.instrumentStore.Instruments$,
            value: editData.InstrumentId,
            sort: 1,
            valuePrimitive: true
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanBookingEditData>('DriverId'),
            label: 'Docu_Ini__Motorist',
            textField: nameOf<IDriver>('FirstNameFullName'),
            valueField: nameOf<IDriver>('Id'),
            options: this.driverStore.Drivers$,
            value: editData.DriverId,
            sort: 1,
            valuePrimitive: true
          }),
          new ApDynformsConfigPlaceholder(),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IFwPlanBookingEditData>('DurationInHours'),
            label: `${this.translationService.translate('Docu_Ini__Duration')} [${this.translationService.translate('Base__UnitH')}]`,
            value: editData.DurationInHours,
            decimals: 1,
            min: 0,
            step: 0.1
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IFwPlanBookingEditData>('HaPerHour'),
            label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
            value: editData.HaPerHour,
            value$: modalDialogChanges$.pipe(
              map(c => {
                if (!Number.isFinite(overviewItem.AreaHa) || !Number.isFinite(c.DurationInHours)) {
                  return 0;
                }
                const result = overviewItem.AreaHa / c.DurationInHours;
                return Number.isFinite(result) ? result : 0;
              })
            ),
            decimals: 1,
            min: 0,
            step: 0.1,
            disabled: true
          })
        ]
      })
    ];
  }

  /**
   * Gets the field configuration for the pager edit
   * @returns Array of fieldset configurations for the pager edit
   * @public
   */
  public getPagerEditFields(): ApDynformsConfigFieldset[] {
    const campaignYear = this.campaignYearStore.SelectedCampaignYear.Year;
    const minAndMaxRange = this.fwEntryComponentService.getMinAndMaxDateRangesForPlanBook(campaignYear);
    return [
      new ApDynformsConfigFieldset({
        key: 'General',
        legend: 'Global__General_Information',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigDatepicker({
            key: nameOf<IFwPlanBookingEditData>('ApplicationDate'),
            label: 'Nutrients__FertilizationDate',
            minDate: minAndMaxRange.Min,
            maxDate: minAndMaxRange.Max
          }),
          new ApDynformsConfigPlaceholder()
        ]
      }),
      new ApDynformsConfigFieldset({
        key: 'Docu',
        legend: 'Settings__Lbl_Menu_Resources',
        useMaxWidth: true,
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanBookingEditData>('MachineId'),
            label: 'Settings__Lbl_Menu_Devices',
            textField: nameOf<IMachine>('NameWithSign'),
            valueField: nameOf<IMachine>('Id'),
            options: this.machineStore.Machines$,
            sort: 1,
            valuePrimitive: true
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanBookingEditData>('InstrumentId'),
            label: 'Docu_Ini__Tool',
            textField: nameOf<IInstrument>('Name'),
            valueField: nameOf<IInstrument>('Id'),
            options: this.instrumentStore.Instruments$,
            sort: 1,
            valuePrimitive: true
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<IFwPlanBookingEditData>('DriverId'),
            label: 'Docu_Ini__Motorist',
            textField: nameOf<IDriver>('FirstNameFullName'),
            valueField: nameOf<IDriver>('Id'),
            options: this.driverStore.Drivers$,
            sort: 1,
            valuePrimitive: true
          }),
          new ApDynformsConfigNumerictextbox({
            key: nameOf<IFwPlanBookingEditData>('HaPerHour'),
            label: `${this.translationService.translate('FieldAreaPerformance')} [${this.translationService.translate('Unit_ha_per_h')}]`,
            decimals: 1,
            min: 0,
            step: 0.1
          })
        ]
      })
    ];
  }
}
